import React, { useState, createContext, useContext, memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from '@rebass/grid';
import styled, { createGlobalStyle } from 'styled-components';
import { media, getThemeProp } from '@amtrav/themes';
import { IconLine } from '@amtrav/icons';
import { Popover, Button, Table, Badge, GatherBadge, CardFlat, Avatar, Checkbox, ApprovalTray, TextInput, DropdownSelect, ToggleGroup, InlineLoader, EmptyState, Card, Heading2 } from '@amtrav/view-components';
import { SPACE, getTokenValue, THEME_TOKENS } from '@amtrav/theme-system';
import reactStringReplace from 'react-string-replace';
import { shortDayMonthDate, parseDate, dateRangeFormatter } from '@amtrav/dates';
import Masonry from 'react-masonry-css';
import { bookingApproveOrReject } from '@amtrav/webservice';
import { wordsToNumbers } from '@amtrav/utils';

const InputsWrapper = styled(Flex)`
  ${media.Medium`
  gap: 12px;
`}
`;
const inputsMediumWidth = 'calc(50% - 6px)';
const InputWithLabel = styled(Box)`
  width: 100%;
  padding-top: 16px;

  ${media.Medium`
    width: ${inputsMediumWidth};
    max-width: 520px;
  `};
`;
const approveTripsLeftRight = [2, 2, '24px'];

// These constants are used to set the Session or Local Storage keys
const VIEW_TYPE = 'viewType';
const TILE_VIEW = 'tile';
const TABLE_VIEW = 'table';
const TRIPS_PAGE = 'Trips';

// LS for LocalStorage
const LS_VIEWTYPE_KEY = `${TRIPS_PAGE}_${VIEW_TYPE}`;
const JSR_TRAVELER_KEY = 'traveler';
const JSR_SORT_TYPE_KEY = 'sortType';
const JSR_SEARCH_KEY = 'search';
const JSR_VIEWTYPE_KEY = 'viewType';
const DEFAULT_SORT_TYPE = 'firstTravelDate';
const RELEVANCE_VALUE = '*relevance*';
const TRIPS_SEARCH_KEY = 'TripsListSearchIndex';
const APPROVALS_SEARCH_KEY = 'TripsApprovalsSearchIndex';
const STATUS_KIND_MAP = {
  CONFIRMED: 'success',
  TICKETED: 'success',
  PENDING: 'warning',
  'NEEDS APPROVAL': 'warning',
  'ON HOLD': 'warning',
  'CANCEL PENDING': 'warning',
  CANCELLED: 'danger',
  CANCELED: 'danger',
  EXPIRED: 'danger',
  REJECTED: 'danger'
};
const TRIPS_CONSTANTS = {
  APPROVALS_SEARCH_KEY,
  DEFAULT_SORT_TYPE,
  JSR_SEARCH_KEY,
  JSR_SORT_TYPE_KEY,
  JSR_TRAVELER_KEY,
  JSR_VIEWTYPE_KEY,
  LS_VIEWTYPE_KEY,
  TRIPS_SEARCH_KEY,
  RELEVANCE_VALUE,
  TABLE_VIEW,
  TILE_VIEW
};

const ListSelectContStyled = styled(Flex)`
  padding: ${SPACE.size12};
  flex-direction: column;
  overflow: hidden;
`;

// styled <button> is used here instead of <option>
// because option elements are difficult to style
const PopoverOption = styled.button`
  border: none;
  border-radius: inherit;
  cursor: pointer;
  font-size: 14px;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  color: ${getThemeProp('global.link--color')};
  text-align: left;
  line-height: 18px;
  background: white;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;

  &:hover {
    background-color: ${getTokenValue(THEME_TOKENS.textInput.color.backgroundHover)};
    border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border-bottom: 0;
  }

  &:first-child:last-child {
    border-radius: 4px !important;
  }
`;

// styled empty pop over option for no invoices
const PopoverOptionNoInvoices = styled.button`
  border: none;
  border-radius: inherit;
  font-size: 14px;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  color: ${getThemeProp('global.greyPalette.600')};
  text-align: left;
  line-height: 18px;
  background: white;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  font-style: italic;

  &:first-child:last-child {
    border-radius: 4px !important;
  }
`;
const OptionRowInvoice = styled.span`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-weight: bold;
`;
const OptionRowValue = styled.span`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const commonPropTypes$1 = {
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  uiStyle: PropTypes.shape({}),
  getOptionDetails: PropTypes.func
};
const parseDetails = detailString => {
  return detailString.split(' for ');
};
const ListSelect = props => {
  const [open, setOpen] = useState(false);
  const [optionDetails, setOptionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    caption,
    onClick,
    iconPrefix,
    getOptionsDetails,
    bn,
    goToReport
  } = props;
  const handleOpenChange = newState => {
    if (!newState) {
      setOpen(newState);
      setLoading(false);
    }
  };
  const getInvoicePopoverOptions = async () => {
    try {
      if (!optionDetails) {
        setLoading(true);
        const opts = await getOptionsDetails();
        setOptionDetails(opts);
        setLoading(false);
      }
      setOpen(true);
    } catch (err) {
      console.warn('Error getting options details', err);
    }
  };
  return /*#__PURE__*/React.createElement(Popover, {
    withPadding: false,
    onOpenChange: handleOpenChange,
    open: open,
    content: /*#__PURE__*/React.createElement(ListSelectContStyled, null, optionDetails && (Object.keys(optionDetails).length > 0 ? Object.entries(optionDetails).map(_ref => {
      let [key, detail] = _ref;
      const [invNumString, valueString] = parseDetails(detail);
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: key
      }, /*#__PURE__*/React.createElement(PopoverOption, {
        onClick: () => onClick(key),
        value: `${key}`
      }, /*#__PURE__*/React.createElement(OptionRowInvoice, null, invNumString), /*#__PURE__*/React.createElement(OptionRowValue, null, valueString)));
    }) : /*#__PURE__*/React.createElement(PopoverOptionNoInvoices, null, /*#__PURE__*/React.createElement(OptionRowValue, null, "No Invoices Available"))), optionDetails && /*#__PURE__*/React.createElement(PopoverOption, {
      onClick: () => goToReport(bn)
    }, /*#__PURE__*/React.createElement(OptionRowInvoice, null, "View Trip Report")))
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    isLoading: loading,
    bordered: true,
    onClick: getInvoicePopoverOptions,
    text: caption,
    iconPrefix: iconPrefix,
    suffixIconLineName: open ? 'ChevronUp' : 'ChevronDown'
  }));
};
ListSelect.propTypes = {
  ...commonPropTypes$1
};
ListSelect.defaultProps = {
  options: []
};

const TripsContext = /*#__PURE__*/createContext({
  removeBookings: () => {},
  fireNotification: () => {},
  fetchInvoicesDetails: () => {},
  goToInvoice: () => {},
  goToReport: () => {},
  updateSearchStringAndJsr: () => {},
  setViewTypeStateAndJsr: () => {},
  updateSortType: () => {},
  isDev: false
});
const TripsProvider = TripsContext.Provider;
const TripsConsumer = TripsContext.Consumer;

const commonPropTypes = {
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  uiStyle: PropTypes.shape({})
};
const InvoiceButton = _ref => {
  let {
    bn,
    invoices,
    lastName
  } = _ref;
  const {
    fetchInvoicesDetails,
    goToInvoice,
    goToReport,
    hideMultipleInvoices
  } = useContext(TripsContext);
  const getOptionsDetails = async () => {
    const rawDict = await fetchInvoicesDetails(bn);
    return Object.fromEntries(Object.entries(rawDict).map(_ref2 => {
      let [key, val] = _ref2;
      return [key, val.description];
    }));
  };
  return /*#__PURE__*/React.createElement(ListSelect, {
    caption: "Invoices",
    options: invoices,
    onClick: invoiceId => goToInvoice(invoiceId, lastName),
    getOptionsDetails: getOptionsDetails,
    goToReport: goToReport,
    goToInvoice: goToInvoice,
    bn: bn,
    hideMultipleInvoices: hideMultipleInvoices,
    lastName: lastName
  });
};
InvoiceButton.propTypes = {
  ...commonPropTypes
};
InvoiceButton.defaultProps = {
  options: [],
  bn: PropTypes.string
};

const Found$2 = styled.span`
  color: ${getThemeProp('global.dark--color')};
  font-weight: bold;
  border-bottom: 1px dashed ${getThemeProp('global.dark--color')};
  background-color: lemonchiffon;
`;
const MobileTd$1 = styled.td`
  display: table-cell;
  line-height: 19px;

  ${media.Medium`
    display: none;
    padding: 10px;
    vertical-align: middle;
    line-height: 1.4;
  `};
`;
const MediumTd$1 = styled.td`
  display: none;

  ${media.Medium`
    display: table-cell;
    position: relative;
  `};
`;
const MediumTh$1 = styled(Table.Head)`
  display: none;

  ${media.Medium`
    display: table-header-group;

    th:first-of-type {
      padding-left: 28px;
    }

    // dates column
    th:nth-child(2) {
      width: 160px;
    }
  `};
`;
const MediumBox = styled(Box)`
  display: none;

  ${media.Medium`
    display: block;
  `};
`;
const TripIcons$1 = styled(Flex)`
  float: right;
  margin-top: 16px;
  font-size: 20px;

  ${media.Medium`
    float: none;
    margin-top: 0;
  `};
`;
const ActionRegion = styled(Flex)`
  min-width: 180px;
`;
const BookingNumberCont = styled(Flex)`
  align-items: center;
  margin-top: 6px;
`;
const BadgeTitleContainer = styled(Flex)`
  gap: ${SPACE.size6};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const TitleCase$1 = styled.span`
  text-transform: capitalize;
`;
const TripTitle$1 = styled.strong`
  max-width: 350px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${getTokenValue(THEME_TOKENS.base.size.bodyText)};

  ${media.XLargeOnly`
    max-width: 450px;
  `}
`;
const TripTitleMobile = styled.strong`
  font-size: ${getTokenValue(THEME_TOKENS.base.size.bodyText)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const BnLink = styled.a`
  text-decoration: underline;
  margin-right: 4px;
`;
const StartDate$1 = styled(Box)`
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-weight: bold;
  white-space: nowrap;
`;
const EndDate$1 = styled(Box)`
  font-size: 14px;
  margin-bottom: -4px;
  white-space: nowrap;
`;
const TravelerName$1 = styled.div`
  font-size: 14px;
  line-height: 1.3;
`;
const GuestContainer$1 = styled(Box)`
  line-height: 1;
`;

// Accessor function for code isolation
// eslint-disable-next-line no-console
const getStatusBadgeKind$1 = status => STATUS_KIND_MAP[status] || console.warn(`${status} is not an accepted status`);
const wrapMatched$2 = (data, pattern, Component, id) => {
  if (typeof data === 'string') {
    const patterns = pattern.split(' ');
    let replacements;
    patterns.forEach(thisPattern => {
      replacements = reactStringReplace(replacements || data,
      // first replace on data, then on replacements
      new RegExp(`(${thisPattern})`, 'i'), (match, i) => /*#__PURE__*/React.createElement(Component, {
        key: `${id}-${thisPattern}-${i}`
      }, match));
    });
    return replacements;
  }
  if (Array.isArray(data)) {
    return data.map(item => wrapMatched$2(item, pattern, Component, id));
  }
  return data;
};
const generateButtons$1 = (bn, invoices, lastName) => {
  return /*#__PURE__*/React.createElement(ActionRegion, {
    width: 1,
    justifyContent: "flex-end"
  }, /*#__PURE__*/React.createElement(MediumBox, {
    pr: 2
  }, /*#__PURE__*/React.createElement(InvoiceButton, {
    bn: bn,
    lastName: lastName,
    invoices: invoices
  })), /*#__PURE__*/React.createElement(Button, {
    text: "View Trip",
    size: "small",
    onClick: () => {
      document.location.href = `/trips/details?bn=${bn}&lastName=${lastName}`;
    }
  }));
};
const generateBnLink = (bn, lastName) => /*#__PURE__*/React.createElement(BnLink, {
  href: `/trips/details?bn=${bn}&lastName=${lastName}`
}, bn);
const TableRow = /*#__PURE__*/memo(_ref => {
  let {
    trip,
    filterString
  } = _ref;
  const {
    bn,
    invoiceNum,
    invoices,
    title,
    firstTravelDate,
    lastTravelDate,
    status,
    meetingId
  } = trip;
  const bookingAttached = trip.bookingAttachment;
  const {
    hasAir,
    hasCar,
    hasHotel,
    hasTrain
  } = bookingAttached;
  function whichIconsToShow(IconToShow) {
    return /*#__PURE__*/React.createElement(Box, {
      ml: 1
    }, IconToShow);
  }
  const tripData = {
    bn,
    invoiceNum,
    title,
    firstTravelDate: shortDayMonthDate(parseDate(firstTravelDate)),
    lastTravelDate: shortDayMonthDate(parseDate(lastTravelDate)),
    status,
    travelers: trip.travelers.map(t => {
      const tName = wrapMatched$2(`${t.lastName}, ${t.firstName}`, filterString.trim(), Found$2, bn);
      return /*#__PURE__*/React.createElement(Flex, {
        key: tName
      }, /*#__PURE__*/React.createElement(TravelerName$1, null, tName), t.isOutsideTraveler ? /*#__PURE__*/React.createElement(GuestContainer$1, {
        ml: 2
      }, /*#__PURE__*/React.createElement(Badge, {
        size: "small",
        noBorderRadius: true,
        kind: "info",
        featured: true
      }, "GUEST")) : null);
    })
  };
  const buttons = generateButtons$1(tripData.bn, invoices, trip.travelers[0].lastName);
  const link = generateBnLink(tripData.bn, trip.travelers[0].lastName);
  const tripIcons = /*#__PURE__*/React.createElement(TripIcons$1, null, hasAir ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Plane",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasCar ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Car",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasHotel ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Hotel",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasTrain ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Train",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null);
  if (filterString.length) {
    Object.keys(tripData).forEach(key => {
      tripData[key] = wrapMatched$2(tripData[key], filterString.trim(), Found$2, tripData.bn);
    });
  }
  return /*#__PURE__*/React.createElement("tr", {
    key: trip.bn
  }, /*#__PURE__*/React.createElement(MobileTd$1, null, /*#__PURE__*/React.createElement(Flex, {
    mb: "6px",
    alignItems: "center"
  }, meetingId ? /*#__PURE__*/React.createElement(GatherBadge, {
    mr: "6px",
    size: 26,
    iconSize: 18,
    iconHeightOffset: -1
  }) : null, /*#__PURE__*/React.createElement(TripTitleMobile, null, tripData.title)), tripData.travelers, /*#__PURE__*/React.createElement(StartDate$1, {
    mt: "6px"
  }, tripData.firstTravelDate, " \u2013 ", tripData.lastTravelDate)), /*#__PURE__*/React.createElement(MobileTd$1, null, buttons, tripIcons), /*#__PURE__*/React.createElement(MediumTd$1, null, /*#__PURE__*/React.createElement(BadgeTitleContainer, {
    alignItems: "center"
  }, meetingId ? /*#__PURE__*/React.createElement(GatherBadge, {
    size: 24,
    iconSize: 14,
    iconHeightOffset: -1
  }) : null, /*#__PURE__*/React.createElement(TripTitle$1, null, tripData.title)), /*#__PURE__*/React.createElement(BookingNumberCont, null, link, tripIcons)), /*#__PURE__*/React.createElement(MediumTd$1, null, /*#__PURE__*/React.createElement(StartDate$1, null, tripData.firstTravelDate), /*#__PURE__*/React.createElement(EndDate$1, {
    mt: "6px"
  }, "to ", tripData.lastTravelDate)), /*#__PURE__*/React.createElement(MediumTd$1, null, tripData.travelers), /*#__PURE__*/React.createElement(MediumTd$1, null, /*#__PURE__*/React.createElement(Badge, {
    kind: getStatusBadgeKind$1(status)
  }, /*#__PURE__*/React.createElement(TitleCase$1, null, status === null || status === void 0 ? void 0 : status.toLowerCase()))), /*#__PURE__*/React.createElement(MediumTd$1, null, buttons));
});
TableRow.propTypes = {
  trip: PropTypes.shape({
    bn: PropTypes.string,
    invoiceNum: PropTypes.string,
    invoices: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number
    })),
    title: PropTypes.string,
    firstTravelDate: PropTypes.string,
    lastTravelDate: PropTypes.string,
    status: PropTypes.string,
    meetingId: PropTypes.number,
    bookingAttachment: PropTypes.shape({
      hasAir: PropTypes.bool,
      hasCar: PropTypes.bool,
      hasHotel: PropTypes.bool,
      hasTrain: PropTypes.bool
    }),
    travelers: PropTypes.arrayOf(PropTypes.shape({
      lastName: PropTypes.string
    }))
  }).isRequired,
  filterString: PropTypes.string.isRequired
};
const TripsListTable = _ref2 => {
  let {
    trips,
    filterString
  } = _ref2;
  const {
    hideMultipleInvoices
  } = useContext(TripsContext);
  const headings = ['Booking', 'Dates', 'Traveler(s)', 'Status', ''];
  const tableHeadings = headings.map(label => /*#__PURE__*/React.createElement("th", {
    key: label
  }, label));
  const tableRows = trips.map(trip => /*#__PURE__*/React.createElement(TableRow, {
    key: trip.bn,
    trip: trip,
    filterString: filterString,
    hideMultipleInvoices: hideMultipleInvoices
  }));
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Table, {
    minimal: true
  }, /*#__PURE__*/React.createElement(MediumTh$1, null, /*#__PURE__*/React.createElement("tr", null, tableHeadings)), /*#__PURE__*/React.createElement(Table.Body, null, tableRows)));
};
TripsListTable.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filterString: PropTypes.string.isRequired
};

const Found$1 = styled.span`
  font-weight: bold;
  text-decoration: underline;
  color: ${getThemeProp('global.warning--color')};
`;
const StyledTripOverviewCard = styled(CardFlat)`
  padding: 0;
  overflow: hidden;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  color: ${getThemeProp('global.textBody--color')};
`;
const Masthead = styled(Box)`
  height: 190px;
  width: 100%;
  background: url('${props => props.imageUrl}') no-repeat;
  background-size: cover;
  background-position: center;
  color: white;
  cursor: pointer;
`;
const PhotoGradient = styled.div`
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 55%,
    rgba(0, 0, 0, 0.65) 81%,
    rgba(0, 0, 0, 0.76) 100%
  );
`;
const FullHeight = styled(Flex)`
  height: 100%;
`;
const TripName = styled.div`
  margin-bottom: 4px;
  font-family: ${getThemeProp('global.headings--fontFamily')};
  font-size: 16px;
  font-weight: 500;
  text-shadow: 1px 1px 2px black;
  text-transform: capitalize;
`;
const Dates = styled.p`
  margin: 0;
  font-size: 14px;
  text-shadow: 1px 1px 2px black;
`;
const CardFooter = styled(Box)`
  background-color: ${getThemeProp('global.light--color')};
  font-size: 12px;
`;
const BookingNumber = styled.span`
  flex-grow: 1;
  font-size: 16px;
  font-weight: 600;
`;
const SmallCaps = styled(Box)`
  font-size: 11px;
  margin-right: 3px;
  /* font alignment is being weird with booking no.
  this is a temporary fix */
  position: relative;
  bottom: -1px;
`;
const SubHead = styled(Flex)`
  background-color: ${getThemeProp('global.light--color')};
`;
const TripIcons = styled(Flex)`
  font-size: 24px;
  margin-bottom: -5px;
`;
const AvatarSmall = styled(Avatar)`
  width: 28px;
  height: 28px;
  font-size: 12px;
`;
const TitleCase = styled.span`
  text-transform: capitalize;
`;

// Accessor function for code isolation
// eslint-disable-next-line no-console
const getStatusBadgeKind = status => STATUS_KIND_MAP[status] || console.warn(`${status} is not an accepted status`);
const wrapMatched$1 = (data, pattern, Component, id) => {
  if (pattern.length < 1) {
    return data;
  }
  if (typeof data === 'string') {
    const patterns = pattern.split(' ');
    let replacements;
    patterns.forEach(thisPattern => {
      replacements = reactStringReplace(replacements || data,
      // first replace on data, then on replacements
      new RegExp(`(${thisPattern})`, 'i'), (match, i) => /*#__PURE__*/React.createElement(Component, {
        key: `${id}-${thisPattern}-${i}`
      }, match));
    });
    return replacements;
  }
  if (Array.isArray(data)) {
    return data.map(item => wrapMatched$1(item, pattern, Component, id));
  }
  return data;
};
const TripOverviewCard = _ref => {
  let {
    trip,
    className,
    filterString
  } = _ref;
  const {
    bn,
    invoices,
    title,
    firstTravelDate,
    lastTravelDate,
    status,
    meetingId
  } = trip;
  const tripData = {
    bn: wrapMatched$1(bn, filterString.trim(), Found$1, bn),
    title: wrapMatched$1(title, filterString.trim(), Found$1, bn),
    dateRange: dateRangeFormatter(parseDate(firstTravelDate), parseDate(lastTravelDate)),
    status: wrapMatched$1(status, filterString.trim(), Found$1, bn),
    travelers: trip.travelers.map(t => {
      const tName = wrapMatched$1(`${t.firstName} ${t.lastName}`, filterString.trim(), Found$1, bn);
      return /*#__PURE__*/React.createElement(Flex, {
        key: tName,
        py: "6px",
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(AvatarSmall, {
        imgUrl: t.profileImageUrl,
        initials: t.initials
      }), /*#__PURE__*/React.createElement(Box, {
        ml: "6px"
      }, tName), t.isOutsideTraveler ? /*#__PURE__*/React.createElement(Box, {
        ml: 2
      }, /*#__PURE__*/React.createElement(Badge, {
        size: "small",
        noBorderRadius: true,
        kind: "info",
        featured: true
      }, "GUEST")) : null);
    })
  };
  const {
    lastName
  } = trip.travelers[0];
  const {
    imageMediumUrl,
    hasAir,
    hasCar,
    hasHotel,
    hasTrain
  } = trip.bookingAttachment;
  function whichIconsToShow(IconToShow) {
    return /*#__PURE__*/React.createElement(Box, {
      ml: 2
    }, IconToShow);
  }
  const handleCityImgClick = () => {
    window.location.assign(`/trips/details?bn=${bn}&lastName=${lastName}`);
  };
  return /*#__PURE__*/React.createElement(StyledTripOverviewCard, {
    className: className,
    key: tripData.bn
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Masthead, {
    imageUrl: imageMediumUrl,
    onClick: handleCityImgClick
  }, /*#__PURE__*/React.createElement(PhotoGradient, null, /*#__PURE__*/React.createElement(FullHeight, {
    flexDirection: "column",
    height: 1,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    m: 1
  }, /*#__PURE__*/React.createElement(Badge, {
    kind: getStatusBadgeKind(status)
  }, /*#__PURE__*/React.createElement(TitleCase, null, status === null || status === void 0 ? void 0 : status.toLowerCase()))), /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(Box, {
    p: 2
  }, /*#__PURE__*/React.createElement(TripName, null, tripData.title), /*#__PURE__*/React.createElement(Dates, null, tripData.dateRange)), meetingId ? /*#__PURE__*/React.createElement(Box, {
    p: 2
  }, /*#__PURE__*/React.createElement(GatherBadge, {
    size: 30,
    iconSize: 20,
    iconHeightOffset: -1
  })) : null)))), /*#__PURE__*/React.createElement(SubHead, {
    p: "10px",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(SmallCaps, null, "BOOKING # "), /*#__PURE__*/React.createElement(BookingNumber, null, tripData.bn), /*#__PURE__*/React.createElement(TripIcons, null, hasAir ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Plane",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasCar ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Car",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasHotel ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Hotel",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null, hasTrain ? whichIconsToShow(/*#__PURE__*/React.createElement(IconLine, {
    icon: "Train",
    size: 20,
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  })) : null)), /*#__PURE__*/React.createElement(Box, {
    p: 2
  }, tripData.travelers), /*#__PURE__*/React.createElement(CardFooter, {
    px: 2,
    py: "8px"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "flex-end"
  }, /*#__PURE__*/React.createElement(Box, {
    mr: 1
  }, /*#__PURE__*/React.createElement(InvoiceButton, {
    bn: bn,
    lastName: lastName,
    invoices: invoices
  })), /*#__PURE__*/React.createElement(Button, {
    text: "View/Modify" // update this text to "View/Change" when manage-trips feature is launched
    ,
    size: "small",
    onClick: () => {
      document.location.href = `/trips/details?bn=${bn}&lastName=${lastName}`;
    }
  })))));
};
TripOverviewCard.propTypes = {
  trip: PropTypes.shape({
    title: PropTypes.string.isRequired,
    bn: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    meetingId: PropTypes.number,
    invoices: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number
    })),
    travelers: PropTypes.arrayOf(PropTypes.shape({
      lastName: PropTypes.string
    })).isRequired,
    firstTravelDate: PropTypes.string.isRequired,
    lastTravelDate: PropTypes.string.isRequired,
    bookingAttachment: PropTypes.shape({
      bookNum: PropTypes.string.isRequired,
      hasAir: PropTypes.bool,
      hasCar: PropTypes.bool,
      hasHotel: PropTypes.bool,
      hasTrain: PropTypes.bool,
      imageMediumUrl: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  className: PropTypes.string,
  filterString: PropTypes.string
};
TripOverviewCard.defaultProps = {
  className: undefined,
  filterString: ''
};

// Pull these from themes?
const breakpointColumnsObj = {
  default: 4,
  1350: 3,
  1023: 2,
  500: 1
};
const MasonryContainer = styled(Box)`
  .my-masonry-grid {
    display: flex;
    width: auto;
  }
  .my-masonry-grid_column {
    background-clip: padding-box;
  }

  /* Style your items */
  .my-masonry-grid_column > div {
    /* change div to reference your elements you put in <Masonry> */
  }
`;
const BookingCard = _ref => {
  let {
    booking,
    filterString
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    px: [0, '6px'],
    pb: [2, 2, '20px'],
    my: [2, 2, 0]
  }, /*#__PURE__*/React.createElement(TripOverviewCard, {
    trip: booking,
    filterString: filterString
  }));
};
BookingCard.propTypes = {
  booking: PropTypes.shape({}).isRequired,
  filterString: PropTypes.string
};
BookingCard.defaultProps = {
  filterString: ''
};
const TripsListCards = /*#__PURE__*/memo(_ref2 => {
  let {
    trips,
    filterString
  } = _ref2;
  const processedTrips = trips.map(b => /*#__PURE__*/React.createElement(BookingCard, {
    booking: b,
    filterString: filterString,
    key: b.bn
  }));
  return /*#__PURE__*/React.createElement(Box, {
    ml: [0, 0, '-8px'],
    pt: ['2px', '2px', '10px']
  }, /*#__PURE__*/React.createElement(MasonryContainer, {
    width: 1
  }, /*#__PURE__*/React.createElement(Masonry, {
    breakpointCols: breakpointColumnsObj,
    className: "my-masonry-grid",
    columnClassName: "my-masonry-grid_column"
  }, processedTrips)));
});
TripsListCards.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filterString: PropTypes.string
};
TripsListCards.defaultProps = {
  filterString: ''
};

const Found = styled.span`
  color: ${getThemeProp('global.dark--color')};
  font-weight: bold;
  border-bottom: 1px dashed ${getThemeProp('global.dark--color')};
  background-color: lemonchiffon;
`;
const ApprovalTh = styled.th`
  &:has([type='checkbox']) label {
    transform: translateX(-2px);
  }
`;
const MobileTd = styled.td`
  display: table-cell;
  line-height: 19px;

  ${media.Medium`
    display: none;
    padding: 10px;
    vertical-align: middle;
    line-height: 1.4;
  `};
`;
const MediumTd = styled.td`
  display: none;

  ${media.Medium`
    display: table-cell;
  `};
`;
const MediumTdCheckbox = styled(MediumTd)`
  table tbody tr:has(& input:checked) {
    box-shadow: 0px 0px 0px 2px ${getTokenValue(THEME_TOKENS.base.color.action)} inset,
      0px 0px 0px 1000px ${getTokenValue(THEME_TOKENS.base.color.neutral0)} inset;
  }
`;
const MediumTh = styled(Table.Head)`
  display: none;

  ${media.Medium`
    display: table-header-group;
  `};
`;
const TripTitle = styled.div`
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: ${getTokenValue(THEME_TOKENS.base.size.bodyText)};
`;
const StartDate = styled(Box)`
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-weight: bold;
  white-space: nowrap;
`;
const EndDate = styled(Box)`
  font-size: 14px;
`;
const BookingNum = styled(Box)`
  font-size: ${getTokenValue(THEME_TOKENS.base.size.bodyText)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const TravelerName = styled.div`
  font-size: 14px;
  line-height: 1.3;
`;
const GuestContainer = styled(Box)`
  line-height: 1;
`;
const BookedBy = styled(Box)`
  font-size: 14px;
  font-weight: 100;
  line-height: 1.2;
  /* margin-bottom: -4px; */
`;
const BookedByName = styled(BookedBy)`
  font-weight: 600;
`;
const Divider = styled(Box)`
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, rgba(207, 207, 207, 1) 35%, rgba(255, 255, 255, 1) 100%);
`;
const wrapMatched = (data, pattern, Component, id) => {
  if (typeof data === 'string') {
    const patterns = pattern.split(' ');
    let replacements;
    patterns.forEach(thisPattern => {
      replacements = reactStringReplace(replacements || data,
      // first replace on data, then on replacements
      new RegExp(`(${thisPattern})`, 'i'), (match, i) => /*#__PURE__*/React.createElement(Component, {
        key: `${id}-${thisPattern}-${i}`
      }, match));
    });
    return replacements;
  }
  if (Array.isArray(data)) {
    return data.map(item => wrapMatched(item, pattern, Component, id));
  }
  return data;
};
const generateButtons = (bn, invoiceNum, lastName) => {
  return /*#__PURE__*/React.createElement(Button, {
    text: "Details",
    size: "small",
    onClick: () => {
      document.location.href = `/trips/details?jsr=(approve:!t)&bn=${bn}&lastName=${lastName}`;
    }
  });
};
const UpcomingTripsTable = /*#__PURE__*/memo(_ref => {
  let {
    trips,
    filterString
  } = _ref;
  const [checkedIds, updateCheckedIds] = useState([]);
  const {
    removeBookings,
    fireNotification
  } = useContext(TripsContext);
  const SelectAllCheckbox = /*#__PURE__*/React.createElement(Checkbox, {
    id: "cbSelectAll",
    onChange: e => updateCheckedIds(e.target.checked ? trips.map(trip => trip.bn) : []),
    checked: checkedIds.length === trips.length
  });
  const headings = [SelectAllCheckbox, 'Booking #', 'Title', 'Dates', 'Traveler(s)', 'Price', null];
  const tableHeadings = headings.map(label => /*#__PURE__*/React.createElement(ApprovalTh, {
    key: label
  }, label));
  const tableRows = trips.map(trip => {
    const {
      bn,
      invoiceNum,
      title,
      firstTravelDate,
      lastTravelDate,
      price,
      meetingId,
      bookedBy
    } = trip;
    const tripData = {
      bookedBy,
      bn,
      invoiceNum,
      title,
      firstTravelDate: shortDayMonthDate(parseDate(firstTravelDate)),
      lastTravelDate: shortDayMonthDate(parseDate(lastTravelDate)),
      travelers: trip.travelers.map(t => {
        const tName = wrapMatched(`${t.lastName}, ${t.firstName}`, filterString.trim(), Found, bn);
        return /*#__PURE__*/React.createElement(Flex, {
          key: tName
        }, /*#__PURE__*/React.createElement(TravelerName, null, tName), t.isOutsideTraveler ? /*#__PURE__*/React.createElement(GuestContainer, {
          ml: 2
        }, /*#__PURE__*/React.createElement(Badge, {
          size: "small",
          noBorderRadius: true,
          kind: "info",
          featured: true
        }, "GUEST")) : null);
      }),
      price: `$${price}`
    };
    const buttons = generateButtons(tripData.bn, tripData.invoiceNum, trip.travelers[0].lastName);
    if (filterString.length) {
      Object.keys(tripData).forEach(key => {
        tripData[key] = wrapMatched(tripData[key], filterString.trim(), Found, tripData.bn);
      });
    }
    const checkboxHandler = e => {
      const bookingNumber = e.target.dataset.bn;
      updateCheckedIds(e.target.checked ? [...checkedIds, bookingNumber] : checkedIds.filter(id => id !== bookingNumber));
    };
    return /*#__PURE__*/React.createElement("tr", {
      key: trip.bn
    }, /*#__PURE__*/React.createElement(MobileTd, null, /*#__PURE__*/React.createElement(Box, {
      mr: "-12px"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      id: `cb-${trip.bn}`,
      "data-bn": trip.bn,
      onChange: checkboxHandler,
      checked: checkedIds.includes(trip.bn)
    }))), /*#__PURE__*/React.createElement(MobileTd, null, /*#__PURE__*/React.createElement(Flex, {
      mb: "6px",
      alignItems: "center"
    }, meetingId ? /*#__PURE__*/React.createElement(GatherBadge, {
      mr: "6px",
      size: 26,
      iconSize: 18,
      iconHeightOffset: -1
    }) : null, /*#__PURE__*/React.createElement("strong", null, tripData.title)), tripData.travelers, /*#__PURE__*/React.createElement(Box, {
      mt: "6px"
    }, /*#__PURE__*/React.createElement("strong", null, tripData.firstTravelDate)), /*#__PURE__*/React.createElement(EndDate, null, "to ", tripData.lastTravelDate), /*#__PURE__*/React.createElement(Divider, {
      mt: 2
    }), /*#__PURE__*/React.createElement(BookedBy, null, "Booked by:"), /*#__PURE__*/React.createElement(BookedByName, null, tripData.bookedBy)), /*#__PURE__*/React.createElement(MobileTd, null, buttons), /*#__PURE__*/React.createElement(MediumTdCheckbox, null, /*#__PURE__*/React.createElement(Checkbox, {
      id: `cb-${trip.bn}`,
      "data-bn": trip.bn,
      onChange: checkboxHandler,
      checked: checkedIds.includes(trip.bn)
    })), /*#__PURE__*/React.createElement(MediumTd, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(BookingNum, null, tripData.bn), /*#__PURE__*/React.createElement(BookedBy, {
      mt: SPACE.size4
    }, "Booked by: ", /*#__PURE__*/React.createElement("b", null, tripData.bookedBy)))), /*#__PURE__*/React.createElement(MediumTd, null, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, meetingId ? /*#__PURE__*/React.createElement(GatherBadge, {
      mr: 2,
      size: 30,
      iconSize: 20,
      iconHeightOffset: -1
    }) : null, /*#__PURE__*/React.createElement(TripTitle, null, tripData.title))), /*#__PURE__*/React.createElement(MediumTd, null, /*#__PURE__*/React.createElement(StartDate, null, tripData.firstTravelDate), /*#__PURE__*/React.createElement(EndDate, {
      mt: "6px"
    }, "to ", tripData.lastTravelDate)), /*#__PURE__*/React.createElement(MediumTd, null, tripData.travelers), /*#__PURE__*/React.createElement(MediumTd, null, /*#__PURE__*/React.createElement(TripTitle, null, tripData.price)), /*#__PURE__*/React.createElement(MediumTd, null, buttons));
  });
  const tripsApiHandler = payload => {
    const {
      action,
      bnList
    } = payload;
    const message = `The selected trips were ${action === 'A' ? 'accepted' : 'rejected'}`;
    bookingApproveOrReject(payload).then(() => {
      removeBookings(bnList);
      fireNotification('success', message);
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error(error);
      fireNotification('danger', 'A system error occurred.');
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    px: approveTripsLeftRight,
    pb: "110px"
  }, /*#__PURE__*/React.createElement(Table, {
    minimal: true
  }, /*#__PURE__*/React.createElement(MediumTh, null, /*#__PURE__*/React.createElement("tr", null, tableHeadings)), /*#__PURE__*/React.createElement(Table.Body, null, tableRows))), /*#__PURE__*/React.createElement(ApprovalTray, {
    multipleAvailableToApprove: (trips === null || trips === void 0 ? void 0 : trips.length) > 1,
    disabled: !checkedIds.length,
    list: checkedIds,
    onApprove: tripsApiHandler,
    onReject: tripsApiHandler
  }));
});
UpcomingTripsTable.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filterString: PropTypes.string.isRequired
};

const alphaSort = (list, field, isAscending) => {
  return list.sort((a, b) => {
    const val1 = a[field].trim();
    const val2 = b[field].trim();
    if (val1 < val2) {
      return isAscending ? -1 : 1;
    }
    if (val1 > val2) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};
const dateSort = (list, field, isAscending) => {
  return list.sort((a, b) => {
    const val1 = Date.parse(a[field].trim());
    const val2 = Date.parse(b[field].trim());
    if (val1 < val2) {
      return isAscending ? -1 : 1;
    }
    if (val1 > val2) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};
const travelersSort = (list, field, isAscending) => {
  return list.sort((a, b) => {
    const val1 = a[field][0].lastName;
    const val2 = b[field][0].lastName;
    if (val1 < val2) {
      return isAscending ? -1 : 1;
    }
    if (val1 > val2) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};

const ShowingCount = styled.span`
  font-size: 14px;
  width: 100%;
  font-weight: bold;
  color: ${_ref => {
  let {
    hasNone
  } = _ref;
  return hasNone ? getTokenValue(THEME_TOKENS.base.color.dangerText) : getTokenValue(THEME_TOKENS.textInput.color.placeholderText);
}};
  position: absolute;
  padding-top: 4px;
  padding-right: 4px;
`;
const InputWithLabelShowing = styled(InputWithLabel)`
  position: relative;
`;
const EmptySearch = styled(Flex)`
  align-items: center;
  width: 100%;
  justify-content: center;
`;
const ListGridSwitcher = styled.div`
  position: absolute;
  top: 2px;
  right: 12px;

  ${media.Large`
    right: 32px;
  `};
`;
const headingsSortMap = new Map([['firstTravelDate', {
  label: 'Dates',
  sortFn: dateSort
}], ['bn', {
  label: 'Booking Number',
  sortFn: alphaSort
}], ['title', {
  label: 'Title',
  sortFn: alphaSort
}], ['travelers', {
  label: 'Traveler(s)',
  sortFn: travelersSort
}], ['status', {
  label: 'Status',
  sortFn: alphaSort
}]]);
const Loader = /*#__PURE__*/React.createElement(Flex, {
  width: 1
}, /*#__PURE__*/React.createElement(Box, {
  width: 1,
  py: 4
}, /*#__PURE__*/React.createElement(InlineLoader, null)));
const TripsListContainer = _ref2 => {
  let {
    bookings,
    searchString,
    resetHandler,
    isApprovals,
    viewType,
    sortType,
    isLoading,
    totalRecords
  } = _ref2;
  const [isTableView, setIsTableView] = useState(false);
  const [queuedSearchStr, updateQueuedSearchStr] = useState(searchString); // for debounce
  const {
    setSearchString,
    setViewType,
    setSortType
  } = useContext(TripsContext);
  const viewTypeClickHandler = toggleValue => {
    setViewType(toggleValue);
    setIsTableView(!isTableView);
  };
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setSearchString(queuedSearchStr);
    }, 220);
    return () => clearTimeout(debounceTimeout);
  }, [queuedSearchStr]);
  useEffect(() => {
    // Search String was reset from above; reset text input
    if (searchString === '' && queuedSearchStr !== '') {
      updateQueuedSearchStr('');
    }
  }, [searchString]);
  const sortOptions = function () {
    let filterString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    // Approvals do not have a status
    if (isApprovals) {
      headingsSortMap.delete('status');
    }
    const options = [];
    headingsSortMap.forEach((heading, key) => {
      if (!heading) {
        return;
      }
      options.push({
        label: `${heading.label} (Ascending)`,
        value: `${key}`
      });
      options.push({
        label: `${heading.label} (Descending)`,
        value: `-${key}`
      });
    });
    if (filterString.trim().length > 1) {
      options.push({
        label: 'Relevance',
        value: RELEVANCE_VALUE
      });
    }
    return options;
  };
  const clearSearch = () => {
    updateQueuedSearchStr('');
    resetHandler();
  };
  const clearSearchString = () => setSearchString('');
  const sortHandler = event => {
    const {
      value
    } = event.target;
    setSortType(value);
  };
  const sortedList = useMemo(() => {
    if (sortType === RELEVANCE_VALUE) {
      return bookings;
    }
    const isAscending = sortType.indexOf('-') !== 0;
    const sortKey = isAscending ? sortType : sortType.slice(1);
    return [...headingsSortMap.get(sortKey).sortFn(bookings, sortKey, isAscending)];
  }, [sortType, bookings]);
  const onTextValueChange = _ref3 => {
    let {
      target
    } = _ref3;
    return updateQueuedSearchStr(target.value);
  };
  const renderResults = () => {
    if (!sortedList.length) {
      return /*#__PURE__*/React.createElement(EmptySearch, {
        pt: SPACE.size32
      }, /*#__PURE__*/React.createElement(EmptyState, {
        iconLineName: "InputSearch",
        text: "No Search Results. Please try broadening your search.",
        size: "large",
        secondaryContent: /*#__PURE__*/React.createElement(Box, {
          pt: SPACE.size16
        }, /*#__PURE__*/React.createElement(Button, {
          text: "Show All",
          kind: "secondary",
          onClick: clearSearch
        }))
      }));
    }
    const TripsDisplayMode = viewType === TILE_VIEW ? /*#__PURE__*/React.createElement(TripsListCards, {
      trips: sortedList,
      filterString: searchString
    }) : /*#__PURE__*/React.createElement(TripsListTable, {
      trips: sortedList,
      filterString: searchString
    });
    return isApprovals ? /*#__PURE__*/React.createElement(UpcomingTripsTable, {
      trips: sortedList,
      filterString: searchString
    }) : TripsDisplayMode;
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputsWrapper, {
    pb: ['12px', '12px', SPACE.size12],
    px: isApprovals ? approveTripsLeftRight : 0,
    flexWrap: "wrap",
    width: 1
  }, /*#__PURE__*/React.createElement(InputWithLabelShowing, null, /*#__PURE__*/React.createElement(TextInput, {
    clearable: true,
    onClear: clearSearchString,
    value: queuedSearchStr,
    onChange: onTextValueChange,
    placeholder: "Search trips, travelers, or dates",
    iconLineName: "Filter",
    prefixContent: "Search"
  }), /*#__PURE__*/React.createElement(Flex, null, sortedList.length < totalRecords ? /*#__PURE__*/React.createElement(ShowingCount, {
    hasNone: sortedList.length === 0
  }, "Showing ", sortedList.length, " of ", totalRecords) : null)), /*#__PURE__*/React.createElement(InputWithLabel, null, /*#__PURE__*/React.createElement(DropdownSelect, {
    options: sortOptions(searchString),
    value: sortType,
    onChange: sortHandler,
    iconLineName: sortType.indexOf('-') !== -1 ? 'SortAscending' : 'SortDescending'
  }))), !isApprovals ? /*#__PURE__*/React.createElement(ListGridSwitcher, null, /*#__PURE__*/React.createElement(ToggleGroup, {
    type: "single",
    onValueChange: viewTypeClickHandler,
    defaultValue: viewType
  }, /*#__PURE__*/React.createElement(ToggleGroup.Item, {
    "data-viewtype": TABLE_VIEW,
    value: TABLE_VIEW
  }, /*#__PURE__*/React.createElement(IconLine, {
    icon: "ListLong"
  })), /*#__PURE__*/React.createElement(ToggleGroup.Item, {
    "data-viewtype": TILE_VIEW,
    value: TILE_VIEW
  }, /*#__PURE__*/React.createElement(IconLine, {
    icon: "LayoutGrid"
  })))) : null, /*#__PURE__*/React.createElement(Box, {
    width: [1]
  }, isLoading ? Loader : renderResults()));
};
TripsListContainer.propTypes = {
  bookings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalRecords: PropTypes.number.isRequired,
  isApprovals: PropTypes.bool,
  isLoading: PropTypes.bool,
  viewType: PropTypes.string.isRequired,
  sortType: PropTypes.string.isRequired,
  searchString: PropTypes.string,
  resetHandler: PropTypes.func
};
TripsListContainer.defaultProps = {
  isApprovals: false,
  searchString: '',
  isLoading: false,
  resetHandler: () => {}
};

const GlobalStyle = createGlobalStyle`
  .chat-my-trips {
    height: 100%;
  }
  body.is-our-app {
    .chat-my-trips {
      display: none;
    }
  }
`;
const InfoPanel = styled(Card)`
  background-color: ${getTokenValue(THEME_TOKENS.card.color.background)};
  text-align: center;
  padding: ${SPACE.size24} ${SPACE.size16};
  flex: 1 1 auto;
  max-width: 620px;
`;
const InfoHeading = styled(Heading2)`
  margin-bottom: ${SPACE.size16};
`;
const PhoneNumber = styled.a`
  color: ${getThemeProp('global.link--color')};
  font-size: ${getTokenValue(THEME_TOKENS.base.size.header3)};
  text-decoration: none;
  font-weight: bold;
`;
const PhoneNumberLabel = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  font-size: 14px;
  padding-bottom: ${SPACE.size2};
`;
const FlexGap = styled(Flex)`
  gap: ${_ref => {
  let {
    gap
  } = _ref;
  return gap;
}};
`;
const startProactiveChat = () => {
  return typeof window.startProactiveChat() === 'function' && window.startProactiveChat();
};
const DashboardEmptyState = _ref2 => {
  let {
    usPhone,
    intlPhone,
    navHotels = true,
    navCars = true,
    navFlights = true,
    isPast = false
  } = _ref2;
  const supportBookingOnline = navFlights || navHotels || navCars;
  if (isPast) {
    return /*#__PURE__*/React.createElement(Box, {
      mt: SPACE.size32
    }, /*#__PURE__*/React.createElement(EmptyState, {
      illustrationKey: "freedom",
      transparent: true,
      size: "fullWidthLargeText",
      iconLineName: "History",
      text: "No past trips available"
    }));
  }
  const goToFlights = () => {
    window.location = '/flights/';
  };
  const goToHotels = () => {
    window.location = '/hotels/';
  };
  const goToCars = () => {
    window.location = '/cars/';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GlobalStyle, null), /*#__PURE__*/React.createElement(Box, {
    mt: SPACE.size32
  }, /*#__PURE__*/React.createElement(EmptyState, {
    size: "fullWidthLargeText",
    transparent: true,
    illustrationKey: "customer-care-02",
    iconLineName: "History",
    text: "You have no upcoming trips",
    secondaryContent: /*#__PURE__*/React.createElement(FlexGap, {
      width: 1,
      alignItems: "center",
      gap: SPACE.size24,
      justifyContent: "center",
      mt: SPACE.size48,
      flexDirection: ['column', 'column', 'column', 'column', 'row']
    }, supportBookingOnline ? /*#__PURE__*/React.createElement(InfoPanel, null, /*#__PURE__*/React.createElement(InfoHeading, null, "Book online"), /*#__PURE__*/React.createElement(FlexGap, {
      gap: SPACE.size12,
      flexDirection: ['column', 'column', 'column', 'column', 'row'],
      justifyContent: "center"
    }, navFlights ? /*#__PURE__*/React.createElement(Button, {
      bordered: true,
      prefixIconLineName: "Plane",
      text: "Flights/Rail",
      onClick: goToFlights
    }) : null, navHotels ? /*#__PURE__*/React.createElement(Button, {
      bordered: true,
      prefixIconLineName: "Hotel",
      text: "Hotels",
      onClick: goToHotels
    }) : null, navCars ? /*#__PURE__*/React.createElement(Button, {
      bordered: true,
      prefixIconLineName: "Car",
      text: "Cars",
      onClick: goToCars
    }) : null)) : null, /*#__PURE__*/React.createElement(InfoPanel, null, /*#__PURE__*/React.createElement(InfoHeading, null, "Connect with a travel advisor"), /*#__PURE__*/React.createElement(FlexGap, {
      gap: SPACE.size32,
      justifyContent: "center",
      alignItems: "center",
      flexDirection: ['column', 'column', 'column', 'column', 'row']
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PhoneNumberLabel, null, "Call within USA"), /*#__PURE__*/React.createElement(PhoneNumber, {
      href: `tel:${wordsToNumbers(usPhone)}`
    }, usPhone)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PhoneNumberLabel, null, "Call outside USA"), /*#__PURE__*/React.createElement(PhoneNumber, {
      href: `tel:${wordsToNumbers(intlPhone)}`
    }, intlPhone)), /*#__PURE__*/React.createElement(Box, {
      className: "chat-my-trips"
    }, /*#__PURE__*/React.createElement(Button, {
      bordered: true,
      text: "Chat Now",
      onClick: startProactiveChat
    })))))
  })));
};
DashboardEmptyState.propTypes = {
  usPhone: PropTypes.string.isRequired,
  intlPhone: PropTypes.string.isRequired,
  navHotels: PropTypes.bool,
  navCars: PropTypes.bool,
  navFlights: PropTypes.bool,
  isPast: PropTypes.bool
};

const ApproveTripsEmptyState = () => {
  return /*#__PURE__*/React.createElement(EmptyState, {
    iconLineName: "Checkbox",
    illustrationKey: "travel-companion-01",
    text: "When you have trips to approve, they will appear here",
    size: "fullWidthLargeText",
    transparent: true
  });
};

export { ApproveTripsEmptyState, InputWithLabel, InputsWrapper, TRIPS_CONSTANTS, TripsConsumer, TripsContext, DashboardEmptyState as TripsEmptyState, TripsListContainer, TripsProvider, approveTripsLeftRight };
//# sourceMappingURL=index.esm.js.map
